/**
 * UpgradeMoment v1.2 (2026-10-23, T972: a request with `snapshot` and/or `reportOrder` shows, in order, the Snapshot
 *   the balance covers, then this one Full Report paid once inside the dialog (EmbeddedReportCheckout, price from
 *   public_report_price()), then the plan as the second option)
 * UpgradeMoment v1.1 (2026-09-28, T194: need.tip shown as hover text and a small line)
 * UpgradeMoment v1.0 (2026-09-27, cc_tasks row 82)
 *
 * The one dialog every blocked action opens. It says what the user was trying
 * to do, what that needs, and offers ONE thing to buy (the cheapest pack that
 * covers the shortfall, or the plan that unlocks it), plus "See all plans".
 * Checkout opens in a new tab; this page never moves.
 *
 *   <UpgradeMomentHost/>   mounted once in App.tsx, opened by openUpgradeMoment()
 *   <UpgradeResume/>       dropped on a page that can re-run an action; waits for
 *                          the payment to land, then calls onResume(ref)
 *
 * Offer logic, resume record and tracking: src/lib/upgradeMoment.ts.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { confirmPackPurchase } from "@/lib/creditPacks";
import { supabase } from "@/integrations/supabase/client";
import { EmbeddedReportCheckout } from "@/components/billing/EmbeddedReportCheckout";
import {
  SEE_ALL_PLANS_URL, claimResume, needMet, needSentence, offerLabel, offerTip, onUpgradeMomentRequest,
  peekResume, pickOffer, readBalance, readPacks, startUpgradeCheckout, trackUpgradeEvent,
  type BalanceSnapshot, type ResumeRecord, type UpgradeMomentRequest, type UpgradeOffer,
} from "@/lib/upgradeMoment";

type Phase = "loading" | "ready" | "opening" | "opened" | "error";

// v1.2 (T972): what one Full Report costs, from public_report_price() (pricing settings), never a typed figure.
type ReportPrice = { enabled?: boolean; usd_cents?: number; delivery_minutes?: number };
async function readReportPrice(): Promise<ReportPrice | null> {
  try {
    const { data, error } = await supabase.rpc("public_report_price" as any);
    return error ? null : ((data as ReportPrice) || null);
  } catch { return null; }
}
function reportPriceLine(p: ReportPrice | null): string {
  const usd = p && typeof p.usd_cents === "number" && p.usd_cents > 0 ? `$${(p.usd_cents / 100).toFixed(p.usd_cents % 100 ? 2 : 0)}` : null;
  const ready = p && p.delivery_minutes ? ` Ready in about ${p.delivery_minutes} minutes.` : "";
  return `${usd ? `${usd}, paid once here` : "Paid once here"}. Your plan does not change.${ready}`;
}

export function UpgradeMoment({ request, onClose }: { request: UpgradeMomentRequest | null; onClose: () => void }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [bal, setBal] = useState<BalanceSnapshot | null>(null);
  const [offer, setOffer] = useState<UpgradeOffer | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // v1.2 (T972): this one Full Report, paid inside the dialog; its price is read at runtime, never typed.
  const [paying, setPaying] = useState(false);
  const [reportPrice, setReportPrice] = useState<ReportPrice | null>(null);

  useEffect(() => {
    if (!request) return;
    let live = true;
    setPhase("loading"); setErr(null); setOffer(null); setPaying(false);
    trackUpgradeEvent(request.limitType, "limit_hit");
    (async () => {
      const [b, packs, price] = await Promise.all([
        readBalance().catch(() => null),
        readPacks().catch(() => []),
        request.reportOrder ? readReportPrice() : Promise.resolve(null),
      ]);
      if (!live) return;
      setBal(b);
      setReportPrice(price);
      setOffer(pickOffer(request.need, b, packs));
      setPhase("ready");
    })();
    return () => { live = false; };
  }, [request]);

  const snapForNow = !!(request?.snapshot && bal && bal.spendable >= request.snapshot.credits);
  const secondary = snapForNow || !!request?.reportOrder;

  const buy = async () => {
    if (!request || !offer) return;
    trackUpgradeEvent(request.limitType, "upgrade_click");
    setPhase("opening");
    try {
      await startUpgradeCheckout(offer, request, bal);
      setPhase("opened");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Checkout could not be opened.");
      setPhase("error");
    }
  };

  const frozen = bal?.purchasedFrozen && bal.purchased > 0 && offer?.kind === "plan"
    ? `You also hold ${bal.purchased} pack credits; they become spendable on ${offer.plan.name}.`
    : null;

  return (
    <Dialog open={!!request} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" title="What this needs, and the quickest way to get it">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" title="The action you were about to take">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden />
            {request?.attempting || "Keep going"}
          </DialogTitle>
          <DialogDescription title={request?.need.tip || "What this action needs compared with what your account has now"}>
            {request ? needSentence(request.need, bal) : ""}
          </DialogDescription>
        </DialogHeader>

        {request?.need.tip && <p className="text-xs text-muted-foreground" title={request.need.tip}>{request.need.tip}</p>}
        {frozen && <p className="text-sm text-muted-foreground" title="Pack credits are kept, and unlock on a paid plan">{frozen}</p>}

        {phase === "loading" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" title="Checking your balance and plan">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your account…
          </div>
        )}

        {/* v1.2 (T972): the cheaper thing they can do now, then this one report paid once, then the plan. */}
        {paying && request?.reportOrder ? (
          <EmbeddedReportCheckout
            entityName={request.reportOrder.entityName}
            entityDomain={request.reportOrder.entityDomain}
            pagePath={request.reportOrder.pagePath}
            onBack={() => setPaying(false)}
          />
        ) : null}
        {!paying && phase !== "loading" && (snapForNow || request?.reportOrder) ? (
          <div className="flex flex-col gap-2 pt-1">
            {snapForNow && request?.snapshot ? (
              <Button
                onClick={() => { const s = request.snapshot!; onClose(); s.run(); }}
                className="w-full"
                title={`Runs now and uses ${request.snapshot.credits} of the ${bal?.spendable} credits you have. Nothing to buy.`}
              >
                {request.snapshot.label}
              </Button>
            ) : null}
            {request?.reportOrder && reportPrice?.enabled !== false ? (
              <div className="rounded-md border p-3 space-y-2" title={`The Full Report on ${request.reportOrder.entityName}, bought once`}>
                <p className="text-sm font-medium" title="Around twenty sections, each one researched, written and cited">
                  {`Or get the Full Report on ${request.reportOrder.entityName}, once`}
                </p>
                <p className="text-xs text-muted-foreground" title="The price comes from current pricing; nothing on your plan changes">
                  {reportPriceLine(reportPrice)}
                </p>
                <Button
                  variant={snapForNow ? "outline" : "default"}
                  className="w-full"
                  onClick={() => { trackUpgradeEvent(request.limitType, "upgrade_click"); setPaying(true); }}
                  title={`Pay once for this report here in this window. Your plan does not change.`}
                >
                  Order Full Report
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {paying ? null : phase === "opened" ? (
          <div className="rounded-md border p-3 text-sm" title="Checkout is open in another tab">
            <p className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4 text-green-600" /> Checkout is open in a new tab.</p>
            <p className="mt-1 text-muted-foreground">
              {request?.resume
                ? "When your payment lands, this page picks up right where you left off."
                : "When your payment lands, try again and it will go through."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 pt-1">
            {offer ? (
              <Button onClick={buy} disabled={phase === "opening" || phase === "loading"} title={offerTip(offer)} className="w-full" variant={secondary ? "outline" : "default"}>
                {phase === "opening" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {offerLabel(offer)}
              </Button>
            ) : phase === "ready" ? (
              <p className="text-sm text-muted-foreground" title="Every plan and credit pack is listed on the pricing page">
                Pick the plan or credit pack that fits on the pricing page.
              </p>
            ) : null}
            {err && <p className="text-sm text-destructive" title="What went wrong opening checkout">{err} Try again, or pick from all plans.</p>}
            <a
              href={SEE_ALL_PLANS_URL}
              target="_blank"
              rel="noopener"
              className="text-center text-sm text-primary underline-offset-4 hover:underline"
              title="Compare every plan and credit pack. Opens in a new tab."
            >
              See all plans
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Mounted once in App.tsx. Any call site opens it with openUpgradeMoment(). */
export function UpgradeMomentHost() {
  const [req, setReq] = useState<UpgradeMomentRequest | null>(null);
  useEffect(() => onUpgradeMomentRequest((r) => setReq({ ...r })), []);
  return <UpgradeMoment request={req} onClose={() => setReq(null)} />;
}

// ── resume ─────────────────────────────────────────────────────────────
const POLL_MS = 3000;
const WAIT_MS = 60_000;

type WaitState = "idle" | "waiting" | "late" | "landed";

function samePage(rec: ResumeRecord): boolean {
  try { return new URL(rec.returnTo, window.location.origin).pathname === window.location.pathname; } catch { return false; }
}

/**
 * Put this on a page that can re-run a blocked action. When a resume record for
 * `action` exists and this is the page it came from, it waits (up to ~60 s, with
 * a visible banner when the buyer has just come back from Checkout) for the plan
 * or credits to land, then claims the record and calls onResume(ref) once.
 */
export function UpgradeResume({ action, onResume, canResume }: {
  action: string;
  onResume: (ref: string | null, rec: ResumeRecord) => void;
  /** False when this tab lacks what the action needs (e.g. a fresh tab without the
   *  form the user filled in): the record is then left for the tab that has it. */
  canResume?: (ref: string | null, rec: ResumeRecord) => boolean;
}) {
  // Read the return parameters during the first render, before any other
  // component's effect (CreditBalanceCard's readPackReturn) clears them.
  const [ret] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const back = q.get("upgrade") === "success" || q.get("pack") === "success" || q.get("success") === "true";
    return { back, sessionId: q.get("pack") === "success" ? q.get("session_id") : null };
  });
  const [state, setState] = useState<WaitState>("idle");
  const running = useRef(false);
  const cb = useRef(onResume);
  cb.current = onResume;
  const can = useRef(canResume);
  can.current = canResume;

  const wait = useCallback(async (visible: boolean) => {
    const rec = peekResume();
    if (!rec || rec.action !== action || !samePage(rec) || running.current) return;
    running.current = true;
    if (visible) setState("waiting");
    const until = Date.now() + WAIT_MS;
    try {
      while (Date.now() < until) {
        // The pack grant is idempotent: asking again is safe, and it does not
        // wait for the webhook.
        if (ret.sessionId) { try { await confirmPackPurchase(ret.sessionId); } catch { /* webhook will do it */ } }
        const bal = await readBalance().catch(() => null);
        if (needMet(rec, bal)) {
          if (can.current && !can.current(rec.ref, rec)) {
            // Landed, but the other tab holds the half-finished work.
            setState(visible ? "landed" : "idle");
            return;
          }
          const mine = claimResume(action);
          setState("idle");
          if (mine) {
            toast("Payment landed. Picking up where you left off.");
            cb.current(mine.ref, mine);
          }
          return;
        }
        if (!peekResume()) { setState("idle"); return; } // another tab took it
        await new Promise((r) => setTimeout(r, POLL_MS));
      }
      if (visible) setState("late");
    } finally {
      running.current = false;
    }
  }, [action, ret.sessionId]);

  useEffect(() => {
    if (ret.back) {
      try {
        const q = new URLSearchParams(window.location.search);
        if (q.has("upgrade")) {
          q.delete("upgrade");
          const rest = q.toString();
          window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
        }
      } catch { /* ignore */ }
      void wait(true);
    }
    // The tab the buyer started in: check quietly whenever they come back to it.
    const onFocus = () => { if (document.visibilityState === "visible") void wait(false); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [wait, ret.back]);

  if (state === "idle") return null;
  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-50 flex max-w-md -translate-x-1/2 items-center gap-3 rounded-md border bg-background px-4 py-3 text-sm shadow-lg"
      title="We carry on with what you were doing as soon as your purchase is confirmed"
    >
      {state === "waiting" ? (
        <>
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span>Waiting for your payment to land…</span>
        </>
      ) : state === "landed" ? (
        <span>Payment landed. Your other MentionFox tab picks up right where you left off.</span>
      ) : (
        <>
          <span>Your payment has not landed yet. It can take a minute.</span>
          <Button size="sm" variant="outline" onClick={() => void wait(true)} title="Check again whether your plan or credits have arrived">
            Check again
          </Button>
        </>
      )}
      <button
        type="button"
        onClick={() => setState("idle")}
        className="text-muted-foreground hover:text-foreground"
        title="Hide this. We still pick up where you left off when your payment lands and you come back to this page."
        aria-label="Hide"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
